/**
 * AuthContext — JWT httpOnly Cookie + React state
 *
 * Sécurité :
 *  - Le JWT est posé côté serveur en cookie httpOnly/Secure → non lisible par JS (protection XSS)
 *  - Le token est aussi conservé EN MÉMOIRE (React state) pour les besoins WebSocket/fichiers
 *  - Plus de token en localStorage
 *  - L'impersonation utilise sessionStorage (effacé à la fermeture du navigateur)
 *  - axios envoie automatiquement le cookie (withCredentials: true)
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext();
const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IMPERSONATION_KEY = 'crm_impersonation';

// ── Axios global : cookie envoyé automatiquement sur chaque requête ─────────
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  // Token en mémoire uniquement (pour WebSocket + header explicit)
  const [token, setToken]       = useState(null);
  const [user, setUser]         = useState(null);
  const [loading, setLoading]   = useState(true);

  // Impersonation — sessionStorage (plus sûr que localStorage, effacé à la fermeture)
  const [impersonation, setImpersonation] = useState(() => {
    try {
      const stored = sessionStorage.getItem(IMPERSONATION_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });

  const isImpersonating = !!impersonation;

  // Garde anti-race : passe à false dès qu'un login() démarre, pour empêcher
  // la résolution tardive de restore()/auth/me (session cookie précédente)
  // d'écraser l'utilisateur fraîchement connecté.
  const restoringRef = useRef(true);

  // Synchronise le header Authorization dès que le token change
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Au démarrage : tenter de restaurer la session depuis le cookie httpOnly
  useEffect(() => {
    const restore = async () => {
      // Si impersonation active en sessionStorage, on la restaure directement
      if (impersonation) {
        setToken(impersonation.token);
        setUser(impersonation.user);
        axios.defaults.headers.common['Authorization'] = `Bearer ${impersonation.token}`;
        setLoading(false);
        return;
      }
      // Sinon : /auth/me utilise le cookie httpOnly automatiquement
      try {
        const { data } = await axios.get(`${API_URL}/auth/me`);
        if (!restoringRef.current) return; // un login() a démarré pendant l'appel
        setUser(data);
        // /auth/me retourne le token brut extrait du cookie → on le stocke en mémoire
        // pour le WebSocket et tout code qui a besoin du token en clair
        if (data.token) {
          setToken(data.token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
        }
      } catch {
        // Pas de session → l'utilisateur doit se reconnecter
      } finally {
        if (restoringRef.current) setLoading(false);
      }
    };
    restore();
  }, []); // pas de deps - exécuté une seule fois au montage

  // ── Login ─────────────────────────────────────────────────────────────────
  const login = async (email, password) => {
    restoringRef.current = false; // annule tout restore() encore en vol
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;

    // Stocker le token EN MÉMOIRE (pour WebSocket + header explicite)
    setToken(newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    // Note: le cookie httpOnly a été posé automatiquement par la réponse du serveur

    // Charger le profil complet (avec allowed_partners, permissions…)
    try {
      const { data: meData } = await axios.get(`${API_URL}/auth/me`);
      setUser(meData);
      return meData;
    } catch {
      setUser(userData);
      return userData;
    }
  };

  // ── Logout ────────────────────────────────────────────────────────────────
  const logout = async () => {
    sessionStorage.removeItem(IMPERSONATION_KEY);
    setImpersonation(null);
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    // Demander au serveur de supprimer le cookie httpOnly
    try { await axios.post(`${API_URL}/auth/logout`); } catch { /* silencieux */ }
  };

  // ── Impersonation ─────────────────────────────────────────────────────────
  const startImpersonation = useCallback(async (targetEmail) => {
    const adminToken = impersonation?.adminToken ?? token;
    const adminUser  = impersonation?.adminUser  ?? user;

    const { data } = await axios.post(
      `${API_URL}/auth/impersonate`,
      { target_email: targetEmail },
      { headers: { Authorization: `Bearer ${adminToken}` } }
    );

    const { token: impToken, user: impUser } = data;
    const state = { token: impToken, user: impUser, adminToken, adminUser };

    sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(state));
    axios.defaults.headers.common['Authorization'] = `Bearer ${impToken}`;
    setImpersonation(state);
    setToken(impToken);
    setUser(impUser);
  }, [token, user, impersonation]);

  const stopImpersonation = useCallback(() => {
    if (!impersonation) return;
    const { adminToken, adminUser } = impersonation;

    sessionStorage.removeItem(IMPERSONATION_KEY);
    axios.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
    setImpersonation(null);
    setToken(adminToken);
    setUser(adminUser);
  }, [impersonation]);

  return (
    <AuthContext.Provider value={{
      user, token, login, logout, loading,
      isImpersonating, impersonation,
      startImpersonation, stopImpersonation,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
